/**
 * /settings/bots — the tenant's assistant bots, one row each. A bot is a teammate given to
 * groups; each person who has it gets their own thread with it. A row opens the bot's own
 * page. The search lives in the URL.
 */
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  BotAvatar,
  Button,
  Input,
  Skeleton,
  StatusIndicator,
  Table,
} from '@servicecompressionllc/sc-design-system';
import { Plus, Search } from '@servicecompressionllc/sc-design-system/icons';
import { AppShell } from '../../components/AppShell.js';
import { useMe } from '../../hooks/useMe.js';
import type { Bot, Group } from '../../lib/types.js';
import { useGroupsListLogic } from '../groups/useGroupsLogic.js';
import { useBotsLogic } from './useBotsLogic.js';

/** A bot nobody can see yet: off, or not given to anyone. */
const BotMark = ({ bot }: { bot: Bot }) =>
  bot.enabled && bot.groupIds.length > 0 ? (
    <BotAvatar size="sm" />
  ) : (
    <span
      aria-hidden
      className="h-6 w-6 flex-none rounded-full border-2 border-dashed border-outline-strong"
    />
  );

/** The bot's groups by name, or that nobody has it yet. */
const givenTo = (bot: Bot, groups: Group[]): string => {
  const names = bot.groupIds.flatMap((id) => groups.find((g) => g.id === id)?.name ?? []);
  return names.length > 0 ? names.join(', ') : 'Not given to anyone yet';
};

const matches = (bot: Bot, query: string): boolean =>
  `${bot.name} ${bot.description}`.toLowerCase().includes(query.toLowerCase());

export const BotsPage = () => {
  const meQuery = useMe();
  const logic = useBotsLogic(meQuery.data?.tenant.id);
  const groups = useGroupsListLogic(meQuery.data?.tenant.id).data ?? [];
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const shown = logic.bots.filter((bot) => matches(bot, query.trim()));

  const search = (value: string): void => {
    setSearchParams(value ? { q: value } : {}, { replace: true });
  };
  const open = (bot: Bot): void => {
    void navigate(`/settings/bots/${bot.id}`);
  };

  return (
    <AppShell>
      <div className="mb-5 flex flex-wrap items-end gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="mb-1.5 text-2xl font-bold text-ink">Bots</h2>
          <p className="max-w-[72ch] text-sm text-ink-muted">
            A bot is a teammate you give to a group. Everyone in the group gets their own, with its
            own memory of them.
          </p>
        </div>
        <Button onClick={() => navigate('/settings/bots/new')}>
          <Plus size={16} aria-hidden className="mr-1.5" />
          New bot
        </Button>
      </div>

      {logic.error && (
        <Banner variant="danger" className="mb-4">
          <Banner.Body>
            <Banner.Title>{logic.error.message}</Banner.Title>
          </Banner.Body>
        </Banner>
      )}

      {meQuery.isLoading || logic.isLoading ? (
        <Skeleton className="h-64 w-full rounded-lg" />
      ) : logic.bots.length === 0 ? (
        <div className="rounded-lg border border-outline bg-surface-elevated py-12 text-center">
          <p className="text-sm text-ink-muted">
            No bots yet. Create one, set it up, then turn it on when you&apos;re ready.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center gap-3">
            <div className="relative w-full max-w-xs">
              <Search
                size={14}
                aria-hidden
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle"
              />
              <Input
                type="search"
                aria-label="Search bots"
                placeholder="Search bots"
                value={query}
                onChange={(e) => search(e.target.value)}
                className="pl-9"
              />
            </div>
            <span className="text-xs text-ink-subtle">
              {logic.bots.length} {logic.bots.length === 1 ? 'bot' : 'bots'}
            </span>
          </div>

          <div className="overflow-hidden rounded-lg border border-outline bg-surface-elevated">
            <Table.Root>
              <Table.Header sticky>
                <Table.Row>
                  <Table.HeaderCell>Bot</Table.HeaderCell>
                  <Table.HeaderCell className="text-right md:text-left">Status</Table.HeaderCell>
                  <Table.HeaderCell className="hidden md:table-cell">Given to</Table.HeaderCell>
                  <Table.HeaderCell align="right" className="hidden md:table-cell">
                    People
                  </Table.HeaderCell>
                  <Table.HeaderCell align="right" className="hidden md:table-cell">
                    Turned off
                  </Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {shown.map((bot) => (
                  <Table.Row
                    key={bot.id}
                    role="link"
                    tabIndex={0}
                    aria-label={bot.name}
                    onClick={() => open(bot)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        open(bot);
                      }
                    }}
                    className="cursor-pointer hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
                  >
                    <Table.Cell className="py-2.5">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <BotMark bot={bot} />
                        <div className="flex min-w-0 flex-col">
                          <span className="truncate font-semibold text-ink">{bot.name}</span>
                          {bot.description && (
                            <span className="max-w-[36ch] truncate text-xs text-ink-subtle">
                              {bot.description}
                            </span>
                          )}
                        </div>
                      </div>
                    </Table.Cell>
                    <Table.Cell className="text-right md:text-left">
                      {bot.enabled ? (
                        <StatusIndicator tone="success" className="text-xs">
                          On
                        </StatusIndicator>
                      ) : (
                        <Badge>Off</Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell
                      className={`hidden md:table-cell ${bot.groupIds.length > 0 ? 'text-ink-muted' : 'text-ink-subtle'}`}
                    >
                      {givenTo(bot, groups)}
                    </Table.Cell>
                    <Table.Cell align="right" className="hidden md:table-cell">
                      {bot.people}
                    </Table.Cell>
                    <Table.Cell align="right" className="hidden text-ink-muted md:table-cell">
                      {bot.turnedOff}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Root>
            {shown.length === 0 && (
              <p className="border-t border-outline px-5 py-6 text-sm text-ink-muted">
                No bots match &ldquo;{query.trim()}&rdquo;.
              </p>
            )}
          </div>
        </>
      )}
    </AppShell>
  );
};
